import { cn } from "./lib/cn";

export interface SkeletonProps {
  /** Kaç satırlık iskelet çizilecek. */
  lines?: number;
  /**
   * `text` (varsayılan) — gövde metni yüksekliğinde ince çizgiler. `row` — ListRow/Table
   * satır yüksekliğinde blok (64px, §3); StatTile grid'i için `stat` — 24px sayı çizgisi.
   */
  variant?: "text" | "row" | "stat";
  className?: string;
}

/**
 * Yükleniyor iskeleti (CLOS-106 `[067]`).
 *
 * ### Neden spinner değil
 *
 * `Spinner` "bir şey oluyor" der; iskelet "şu şekilde bir şey gelecek" der. Liste ve
 * tablo gibi yapısı belli yüzeylerde iskelet, içerik geldiğinde yerleşimin zıplamasını da
 * önlüyor.
 *
 * ### Erişilebilirlik
 *
 * `aria-hidden`: ekran okuyucuya "üç boş kutu" okutmanın değeri yok. Yükleme durumunu
 * duyurmak KAPSAYICININ işi (`role="status"`), iskeletin değil — iki yerden duyurmak
 * kullanıcıya aynı şeyi iki kez söylerdi.
 *
 * ⚠ `motion-reduce`: hareket hassasiyeti olan kullanıcıda nabız animasyonu duruyor.
 */
export function Skeleton({ lines = 3, variant = "text", className }: SkeletonProps) {
  if (variant === "row") {
    return (
      <div aria-hidden="true" className={cn("flex flex-col gap-2", className)}>
        {Array.from({ length: lines }, (_, i) => (
          <div
            key={i}
            data-testid="skeleton-satir"
            className="h-16 w-full rounded-lg bg-muted motion-safe:animate-pulse"
          />
        ))}
      </div>
    );
  }
  if (variant === "stat") {
    return (
      <div
        aria-hidden="true"
        className={cn("h-6 w-16 rounded-sm bg-muted motion-safe:animate-pulse", className)}
      />
    );
  }
  return (
    <div aria-hidden="true" className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          data-testid="skeleton-satir"
          className={cn(
            "h-4 rounded-sm bg-muted motion-safe:animate-pulse",
            // Son satır kısa: gerçek metin blokları da öyle biter.
            i === lines - 1 ? "w-2/3" : "w-full",
          )}
        />
      ))}
    </div>
  );
}
